import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';

// Redux
import {useDispatch, useSelector} from 'react-redux';
import {setUserData} from 'store/actions';

import {Group, Modal, Stack} from '@mantine/core';

// Global components
import SearchInput from 'components/core/Input/SearchInput';
import Button from 'components/core/Button';
import Text from 'components/core/Text';

// Local styles
import {useStyles} from './styles';
import {lowercasedIncludes} from 'utils';
import notification from 'utils/notification';
import AuthUtils from 'utils/auth';
import {isWebClient} from 'utils/buildMode';
import {getSwitchableLocationsRequest, switchLocationRequest} from 'request/auth';

const ChangeOutletModal = ({changeOutletModalOpen, setChangeOutletModalOpen}) => {
	const {t} = useTranslation('translation', {keyPrefix: 'global.header'});
	const {details} = useSelector(({reducerUser}) => reducerUser) || {};

	const dispatch = useDispatch();
	const {classes} = useStyles();

	const [search, setSearch] = useState('');
	const [webLocations, setWebLocations] = useState([]);
	const [switchingId, setSwitchingId] = useState(null);

	// Online build: admins pick from every active location they can access.
	useEffect(() => {
		if (!isWebClient || !changeOutletModalOpen) return;
		getSwitchableLocationsRequest()
			.then(response => {
				const body = response?.data?.locations ? response.data : response;
				setWebLocations((body?.locations || []).map(location => ({
					id: location.id,
					label: location.name,
					timezone: location.timezone,
					organizationName: location.organizationName,
				})));
			})
			.catch(error => notification.error({
				title: 'Could not load locations',
				message: error?.response?.data?.message || error.message,
			}));
	}, [changeOutletModalOpen]);

	const switchWebLocation = async location => {
		try {
			setSwitchingId(location.id);
			const response = await switchLocationRequest(location.id);
			const body = response?.data?.token ? response.data : response;
			if (!body?.token) throw new Error('The location switch response was incomplete.');
			await AuthUtils.setNewSession(body.token);
			dispatch(setUserData({
				selectedLocation: {id: body.location.id, label: body.location.name, timezone: body.location.timezone},
				canSwitchLocation: !!body.canSwitchLocation,
			}));
			// A full reload drops every location-scoped cache and reconnects the socket with the new token.
			window.location.reload();
		} catch (error) {
			setSwitchingId(null);
			notification.error({
				title: 'Could not switch location',
				message: error?.response?.data?.message || error.message,
			});
		}
	};

	const displayedOutlets = (isWebClient ? webLocations : details?.locations)
		?.filter(location => lowercasedIncludes(location.label, search)) || [];

	return (
		<Modal
			centered
			opened={changeOutletModalOpen}
			withCloseButton={false}
			classNames={{
				modal: classes.modalCard,
				body: classes.modalBody,
			}}
			onClose={() => setChangeOutletModalOpen(false)}>
			<Text
				mt={24}
				typography="title-bold-18">
				{t('changeOutlet')}
			</Text>
			<SearchInput
				mt={16}
				width={352}
				placeholer={t('searchOutletPlacheholder')}
				onChangeSearch={setSearch}
			/>
			<Stack
				mt={12}
				spacing={0}
				className={classes.changeOutletContainer}>
				{displayedOutlets.map(location => (
					<Group
						py={8}
						key={location.id}
						position="apart"
						className={classes.changeOutletRow} >
						<Text
							typography="body-regular-16">
							{t('outlet', {name: location.label})}
							{isWebClient && location.organizationName ? ` · ${location.organizationName}` : ''}
						</Text>
						<Button
							loading={switchingId === location.id}
							disabled={switchingId !== null}
							onClick={() => {
								if (isWebClient) {
									switchWebLocation(location);
									return;
								}
								dispatch(setUserData({selectedLocation: location}));
								setTimeout(() =>
									window.location.reload()
								, 100);
							}}>
							{t('chooseOutlet')}
						</Button>
					</Group>
				))}
			</Stack>
		</Modal>
	);
};

ChangeOutletModal.defaultProps = {
	changeOutletModalOpen: false,
	setChangeOutletModalOpen: () => null,
};

ChangeOutletModal.propTypes = {
	changeOutletModalOpen: PropTypes.bool,
	setChangeOutletModalOpen: PropTypes.func,
};

export default ChangeOutletModal;